<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">View / Edit Do Not Calls</h1>
        <div class="modal-actions col-2">
          <el-button
            v-if="!bulkOperationComplete"
            type="primary"
            @click="editOperation"
            :disabled="!fieldsValid"
          >
            Save
          </el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="handleClose"
            >{{ bulkOperationComplete ? "Close" : "Cancel" }}</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="handleClose"
          ></el-button>
        </div>
      </div>
    </div>

    <div class="dialog__body">
      <el-form>
        <recent-audits
				service="Do Not Call List" :record="selectedDoNotCall" />
        <el-input type="text" v-model="editForm.name" placeholder="Name">
          <template slot="prefix">Name</template>
        </el-input>
        <el-input
          placeholder="Enter description"
          v-model="editForm.description"
        >
          <template slot="prefix">Description</template>
        </el-input>
        <!-- ADD NUMBERS SECTION -->
        <div v-if="showFormOnEdit">
          <h2 class="form-section-header">Add numbers</h2>
          <div
            class="el-input el-input-group el-input-group--append el-input--prefix"
          >
            <el-input
              v-model="numberForm.number"
              placeholder="Enter number"
            >
              <template slot="prefix">Number</template>
            </el-input>
            <div class="el-input-group__append" v-if="numIsListed">
              Is listed!
            </div>
          </div>

          <el-input
            placeholder="Enter description"
            v-model="numberForm.description"
          >
            <template slot="prefix">Description</template>
          </el-input>
          <div
            class="inline-form-group"
            v-if="validNumber && !numIsListed && !bulkOperationComplete"
          >
            <el-col :span="11" />
            <el-col :span="5" />
            <el-col :span="8" style="text-align:right;">
              <el-tooltip content="Add" placement="top">
                <el-button circle type="primary" @click="addNumber">
                  <el-icon class="el-icon-plus"></el-icon>
                </el-button>
              </el-tooltip>
              <el-button @click="clearNumberFields" type="danger">
                <el-icon type="danger" class="el-icon-delete" />
              </el-button>
            </el-col>
          </div>
          <h3 class="form-section-header">
            And / Or
          </h3>

          <div id="shortnumbers-summary-block">
            <div class="inline-form-group mt-20">
              <bulk-example :file-path="bulkExampleFile" >
                <el-button
                  slot="hoverRef"
                  type="primary"
                  :disabled="bulkOperationComplete"
                  @click="startImport"
                >
                  Import numbers from csv
                </el-button>
              </bulk-example>
              <el-button
                class="ml-5"
                type="danger"
                icon="el-icon-close"
                v-if="fileSelected"
                @click="closeImporting"
              />
              <el-button
                class="ml-5"
                type="primary"
                icon="el-icon-check"
                v-if="csvReady"
                @click="addToBulk"
              />
            </div>
            <div class="csv-import" v-show="importVisible">
              <upload-csv
                :key="uploadCsvKey"
                ref="fileButton"
                v-model="parsedCSV"
                :map-fields="[
                  { label: 'Number', key: 'number' },
                  { label: 'Description', key: 'description' }
                ]"
                @input="csvReady = true"
                @invalidFileType="handleInvalidFileType"
                @fileSelected="submitForMapping"
                :headers="true"
              />
            </div>
          </div>
        </div>
      </el-form>

      <!-- NUMBERS TABLE -->
      <div v-if="bulkNumbers.length">
        <div id="operation-summary" v-if="bulkOperationComplete">
          <div v-if="!!successCount" class="inline-form__group">
            <el-button type="success" icon="el-icon-check" size="mini">
              <span class="black-text">
                {{ successCount }}
              </span>
            </el-button>
          </div>
          <div v-if="!!errorCount" class="inline-form__group">
            <el-button type="danger" icon="el-icon-close" size="mini">
              <span class="black-text">
                {{ errorCount }}
              </span> </el-button
            >&nbsp;
          </div>
        </div>
        <div class="inline-form-group">
          <el-col :span="12">
            <el-input v-model="search" placeholder="Search" clearable>
            </el-input>
          </el-col>
        </div>
        <!-- BULK NUMBERS TABLE -->
        <div class="vxe-table">
          <vxe-grid
            round
            class="bulk-numbers-table"
            border="inner"
            height="350px"
            auto-resize
            empty-text="No Data"
            :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
            :data="filteredBulkNumbers"
            :columns="tableColumns"
          />
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import uploadCSV from "@/components/upload-csv";
import bulkExample from '@/components/bulkExample';
import { mapActions } from "vuex";
import feathersClient from "../../../../feathers/feathers-client";
import RecentAudits from '@/components/RecentAudits';
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: "DoNotCallsEditForm",

  props: {
    editVisible: { required: true },
    selectedDoNotCall: { required: true }
  },

  components: {
    UploadCsv: uploadCSV,
    bulkExample,
    RecentAudits
  },

  data() {
    return {
      bulkExampleFile: 'https://global-app.s3.amazonaws.com/omnia-assets/bulk_dnc_example.csv',
      bulkNumbersChanged: false,
      uploadCsvKey: "",
      showFormOnEdit: true,
      search: "",
      successCount: 0,
      errorCount: 0,
      closeModalContext: "Cancel and Discard Unsaved Changes?",
      editForm: {
        name: "",
        description: ""
      },
      numberForm: { number: "", description: "" },
      bulkOperationComplete: false,
      // ⭐️ Bulk Data ⭐️ //
      bulkNumbers: [],
      parsedCSV: null,
      fieldsValid: false,
      importVisible: false,
      csvReady: false,
      fileSelected: false,
      numIsListed: false,
      tableColumns: [
        { type: "seq", width: 40 },
        {
          field: "number",
          title: "Number",
          minWidth: "120",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.numberFormatter(row.number)}</span>];
            }
          }
        },
        {
          field: "description",
          title: "Description",
          minWidth: 100,
          sortable: true,
          showOverflow: true
        },
        {
          field: "status",
          title: "",
          slots: {
            default: ({ row }) => {
              return [
                <div>
                  {row.status === "done" && (
                    <el-button
                      type="success"
                      class="table-action-button"
                      icon="el-icon-check"
                      size="mini"
                    />
                  )}
                  {row.status === "loading" && (
                    <el-button
                      type="primary"
                      class="table-action-button"
                      icon="el-icon-loading"
                      size="mini"
                    />
                  )}
                  {row.status === "error" && (
                    <el-tooltip placement="top" content={row.errorMessage}>
                      <el-button
                        type="danger"
                        class="table-action-button"
                        icon="el-icon-close"
                        size="mini"
                      />
                    </el-tooltip>
                  )}
                </div>
              ];
            }
          }
        },
        {
          field: "remove",
          title: "",
          slots: {
            default: ({ row, rowIndex }) => {
              return [
                <div>
                  {row.status === "" && (
                    <el-button
                      onClick={() => this.removeFromBulk(rowIndex)}
                      class="table-action-button"
                      type="default"
                      icon="el-icon-minus"
                      size="mini"
                    />
                  )}
                </div>
              ];
            }
          }
        }
      ]
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    fieldLength() {
      return this.editForm.name && this.bulkNumbers.length;
    },

    validNumber() {
      return !!this.numberForm.number.length;
    },

    filteredBulkNumbers() {
      return this.bulkNumbers.filter(
        data =>
          !this.search ||
          data.number.toLowerCase().includes(this.search.toLowerCase())
      );
    },

    number() {
      return this.numberForm.number;
    }
  },

  watch: {
    number(val) {
      let number = "";
      if (val) {
        number = val.replace("(", "");
        number = number.replace(")", "");
        number = number.replace(/-/g, "");
      }
      if (this.bulkNumbers.map(data => data.number).includes(number)) {
        this.numIsListed = true;
      } else {
        this.numIsListed = false;
      }
    },

    fieldLength(val) {
      this.fieldsValid = !!val;
    },

    bulkOperationComplete(val) {
      if (val) {
        this.closeModalContext = `Close the edit modal?`;
      }
    },

    selectedDoNotCall(val) {
      if (val) {
        const { name, description, numbers } = val;
        this.editForm.name = name;
        this.editForm.description = description;

        if (numbers.length) {
          this.bulkNumbers = numbers.map(({ number, description }) => ({
            number,
            description,
            status: "",
            errorMessage: ""
          }));
        }
      }
    }
  },

  methods: {

    numberFormatter(number) {
      return phoneNumberFormatter(number);
    },

    addNumber() {
      const numberBody = { ...this.numberForm };
      numberBody.number = numberBody.number.replace("(", "");
      numberBody.number = numberBody.number.replace(")", "");
      numberBody.number = numberBody.number.replace(/-/g, "");
      this.bulkNumbersChanged = true;
      this.bulkNumbers.push({
        ...numberBody,
        status: ""
      });
      this.numberForm = {
        number: "",
        description: ""
      };
    },

    handleClose() {
      this.cancelOperation();
    },

    cancelOperation() {
      this.clearFields();
      this.$emit("closeModal");
    },

    clearFields() {
      this.bulkNumbersChanged = false;
      this.uploadCsvKey = "";
      this.showFormOnEdit = true;
      this.search = "";
      this.errorCount = 0;
      this.successCount = 0;
      this.bulkNumbers = [];
      this.editForm = {
        name: "",
        description: ""
      };

      this.bulkOperationComplete = false;
      this.closeModalContext = "Cancel and discard unsaved changes?";

      this.parsedCSV = null;
      this.csvReady = false;
      this.fileSelected = false;
      this.importVisible = false;
      this.fieldsValid = false;
      this.bulkNumbers = [];
      this.numberForm = {
        number: "",
        description: ""
      };
      this.numIsListed = false;
    },

    // ⭐️ Methods for Bulk Operation ⭐️ //
    editOperation() {
      this.$confirm("Edit do not call list?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel"
      })
        .then(async () => {
          const editBody = {
            tenantId: this.selectedTenant.id,
            ...this.editForm,
            dncNumbers: this.bulkNumbers
          };
          this.showFormOnEdit = false;
          await feathersClient.service('donotcalls').patch(this.selectedDoNotCall.id, editBody);
          this.handleClose();
        })
        .catch(error => {
          if (error.hasOwnProperty("errors")) {
            this.$notify({
              title: error.message,
              message: error.errors[Object.keys(error.errors)[0]],
              type: "error"
            });
          } else {
            this.$notify({
              title: error.name,
              message: error.message,
              type: "error"
            });
          }
        });
    },

    closeImporting() {
      this.parsedCSV = null;
      this.csvReady = false;
      this.fileSelected = false;
      this.importVisible = false;
      this.$refs.fileButton.sample = null;
      this.$refs.fileButton.fileSelected = false;
      this.$refs.fileButton.map = {};
      this.$refs.fileButton.isValidFileMimeType = false;
      this.$refs.fileButton.form = { csv: null };
      let fileButton = document.querySelector(".form-control-file");
      fileButton.value = null;
    },

    addToBulk() {
      const values = [...this.parsedCSV];
      for (let i = 0; i < values.length; i++) {
        if (
          !this.bulkNumbers.map(data => data.number).includes(values[i].number)
        ) {
          this.bulkNumbers.push({
            ...values[i],
            status: "",
            errorMessage: ""
          });
        }
      }
      this.bulkNumbersChanged = true;
      this.closeImporting();
    },

    startImport() {
      this.importVisible = true;
      const fileButton = document.querySelector(".form-control-file");
      fileButton.click();
    },

    removeFromBulk(index) {
      this.bulkNumbersChanged = true;
      this.bulkNumbers.splice(index, 1);
    },

    submitForMapping() {
      this.fileSelected = true;
      this.$refs.fileButton.load();
    },

    getRandomKey() {
      const randomString = () =>
        Math.random()
          .toString(36)
          .slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },

    handleInvalidFileType() {
      this.closeImporting();
      this.uploadCsvKey = this.getRandomKey();
      this.$notify({
        title: "Invalid file type",
        message: "Please import csv files only!",
        type: "error"
      });
    },

    populateTable() {
      if (this.$refs.xGrid) {
        this.$refs.xGrid.loadData(this.filteredBulkNumbers);
      }
    },

    clearNumberFields() {
      this.numberForm.number = "";
      this.numberForm.description = "";
    }
  },

  created() {
    this.uploadCsvKey = this.getRandomKey();
  }
};
</script>

<style lang="scss" scoped>
#shortnumbers-summary-block {
  max-width: 600px;
  margin: 10px auto;
}

#operation-summary {
  max-width: 600px;
  margin: 10px auto;
  span {
    font-size: 18px;
  }
}

#error-summary {
  max-width: 600px;
  margin: 30px auto;
}

.modal-summary-table {
  max-width: 600px;
  margin: 20px auto;
}

.vue-csv-uploader {
  input[type="file"] {
    display: none;
  }
}

.vxe-table {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}
</style>
